/* =====================================================================
   tower/driver/src/inspection-catalog.js — the TRIP INSPECTION REPORT's fixed item list (Parik 2026-10-23).

   The sample form, item for item: 45 bus/truck/tractor items and 15 trailer items (the same list for Trailer #1
   and #2). FIXED — not company-editable — so every report is comparable. Each item is a stable code; a report
   stores the code, never its words. The phone words an item from its catalog (insp.t.<code> for the truck,
   insp.tr.<code> for a trailer — English and Spanish in tower/i18n); the English here is the printed form's,
   for the design build's store and the office report. The server holds the same list
   (RoutingApp.Core.Entities.TripInspectionCatalog) and refuses a code that is not on it; a test keeps the two equal.

   Groups are how the phone lists the truck items for a fast pick (search covers every item regardless).
   ===================================================================== */

// i18n-exempt-start — the printed form's English (the office's record); the phone shows insp.t.* / insp.tr.* from its catalog
/** [code, English, group] — the form's order (three columns, top to bottom). */
export const TRUCK = Object.freeze([
  ['air_compressor', 'Air Compressor', 'brakes_air'],
  ['air_lines', 'Air Lines', 'brakes_air'],
  ['axles', 'Axles', 'drivetrain'],
  ['batteries', 'Battery(s)', 'electrical'],
  ['belts_hoses', 'Belts/Hoses', 'engine'],
  ['body_frame', 'Body/Frame', 'chassis'],
  ['brakes_adjustment', 'Brakes/Adjustment', 'brakes_air'],
  ['brakes_service', 'Brakes - Service System', 'brakes_air'],
  ['brakes_parking', 'Brakes - Parking System', 'brakes_air'],
  ['charging_system', 'Charging System', 'electrical'],
  ['clutch', 'Clutch', 'drivetrain'],
  ['cooling_system', 'Cooling System', 'engine'],
  ['coupling_devices', 'Coupling Devices', 'chassis'],
  ['documents', 'Documents (insurance, permits, etc.)', 'safety'],
  ['doors_compartments', 'Doors/Compartments', 'cab'],
  ['drive_lines', 'Drive Lines', 'drivetrain'],
  ['emergency_equipment', 'Emergency Equipment', 'safety'],
  ['emergency_exits', 'Emergency Windows/Exits', 'safety'],
  ['engine', 'Engine', 'engine'],
  ['exhaust_system', 'Exhaust System', 'engine'],
  ['fire_extinguishers', 'Fire Extinguishers', 'safety'],
  ['first_aid_kit', 'First Aid Kit', 'safety'],
  ['fluid_leaks', 'Fluid Leaks', 'engine'],
  ['frame', 'Frame', 'chassis'],
  ['fuel_system', 'Fuel System', 'engine'],
  ['heaters_defrosters', 'Heaters/Defrosters', 'cab'],
  ['horns', 'Horns', 'electrical'],
  ['inspection_decal_plates', 'Inspection Decal/Licence Plates', 'safety'],
  ['interior_lights', 'Interior Lights', 'electrical'],
  ['lights_reflectors', 'Lights/Reflectors', 'electrical'],
  ['load_security_devices', 'Load Security Devices', 'safety'],
  ['lubrication_systems', 'Lubrication System(s)', 'engine'],
  ['mirrors', 'Mirrors', 'cab'],
  ['mud_flaps', 'Mud Flaps', 'chassis'],
  ['oil_pressure', 'Oil Pressure', 'engine'],
  ['rear_end', 'Rear End', 'drivetrain'],
  ['recording_devices', 'Recording Device(s)', 'electrical'],
  ['seats', 'Seats', 'cab'],
  ['suspension', 'Suspension', 'chassis'],
  ['steering_mechanism', 'Steering Mechanism', 'chassis'],
  ['transmissions', 'Transmission(s)', 'drivetrain'],
  ['wheels_tires_studs', 'Wheels/Tires/Studs', 'chassis'],
  ['windows_visibility', 'Windows/Visibility', 'cab'],
  ['wipers_washers', 'Wipers/Washers', 'cab'],
  ['other', 'Other', 'other'],
]);

/** A trailer — the same 15 items for Trailer #1 and #2. */
export const TRAILER = Object.freeze([
  ['air_lines_glad_hands', 'Air Lines/Glad Hands'],
  ['axles', 'Axles'],
  ['body_frame', 'Body/Frame'],
  ['brakes_adjustment', 'Brakes/Adjustment'],
  ['coupling_devices', 'Coupling Devices'],
  ['coupling_kingpin', 'Coupling - Kingpin'],
  ['doors_compartments', 'Doors/Compartments'],
  ['inspection_decal_plates', 'Inspection Decal/Plates'],
  ['landing_gear', 'Landing Gear'],
  ['lights_reflectors', 'Lights/Reflectors'],
  ['load_security_dunnage', 'Load Security/Dunnage'],
  ['mud_flaps', 'Mud Flaps'],
  ['suspension', 'Suspension'],
  ['wheels_tires_studs', 'Wheels/Tires/Studs'],
  ['other', 'Other'],
]);
// i18n-exempt-end

/** The truck groups, in the order the phone shows them. */
export const GROUPS = Object.freeze(['brakes_air', 'engine', 'drivetrain', 'electrical', 'chassis', 'cab', 'safety', 'other']);

export const SECTIONS = Object.freeze(['truck', 'trailer1', 'trailer2']);

/** The catalog key of an item's words: insp.t.<code> (truck) / insp.tr.<code> (a trailer). */
export const itemKey = (section, code) => (section === 'truck' ? 'insp.t.' : 'insp.tr.') + code;

/** The printed form's English for an item (the office report, the design build's store). */
export function english(section, code) {
  const row = (section === 'truck' ? TRUCK : TRAILER).find((x) => x[0] === code);
  return row ? row[1] : code;
}

// The office (same page) lays out the report from the same list in the design build.
if (typeof window !== 'undefined') {
  window.TripInspectionCatalog = Object.freeze({
    truck: TRUCK.map(([code, en, group]) => ({ code, english: en, group })),
    trailer: TRAILER.map(([code, en]) => ({ code, english: en })),
    groups: GROUPS, english,
  });
}
